import { MenuProps } from '@mui/material';
import { createContext, useCallback, useContext } from 'react';

import { CONFIRMATION_MESSAGE } from '@/navigation/UnloadPrompt.provider';
import { OVERLAY_MD } from '@/styles/tokens/panelWidths';

import { SideDrawer, SideDrawerProps } from './SideDrawer';

/**
 * Context to provide default MenuProps to select inputs inside a side drawer.
 * This fixes z-index conflicts by disabling portal rendering.
 */
const FormSideDrawerMenuPropsContext = createContext<Partial<MenuProps> | null>(
  null
);

const SIDE_DRAWER_MENU_PROPS: Partial<MenuProps> = {
  disablePortal: true,
};

/**
 * Hook to get MenuProps for select inputs inside a FormSideDrawer.
 * Returns undefined when not inside a FormSideDrawer, allowing normal portal behavior.
 */
export function useFormSideDrawerMenuProps(): Partial<MenuProps> | undefined {
  return useContext(FormSideDrawerMenuPropsContext) ?? undefined;
}

export interface FormSideDrawerProps extends SideDrawerProps {
  /**
   * When true, shows a confirmation dialog before closing if the user has unsaved changes
   */
  shouldBlockClose?: boolean;
}

/**
 * A SideDrawer variant for forms that supports blocking close when there are unsaved changes.
 * This mirrors the behavior of FormModal.
 */
export function FormSideDrawer({
  shouldBlockClose,
  isOpen,
  onClose,
  ...rest
}: FormSideDrawerProps) {
  const safeClose = useCallback(() => {
    if (shouldBlockClose && isOpen) {
      const isConfirmed = window.confirm
        ? window.confirm(CONFIRMATION_MESSAGE)
        : true;

      if (!isConfirmed) {
        return;
      }
    }

    onClose?.();
  }, [isOpen, onClose, shouldBlockClose]);

  return (
    <FormSideDrawerMenuPropsContext.Provider value={SIDE_DRAWER_MENU_PROPS}>
      <SideDrawer
        width={OVERLAY_MD}
        isOpen={isOpen}
        onClose={safeClose}
        {...rest}
      />
    </FormSideDrawerMenuPropsContext.Provider>
  );
}
